import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  Box,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { formatCurrencySync } from '../../utils/currencyUtils';

// FDI Tooth Notation - Permanent teeth (1-4 quadrants, 1-8 teeth per quadrant)
// Quadrant 1: Upper Right (11-18) - displayed 8-1 (18 to 11)
// Quadrant 2: Upper Left (21-28) - displayed 1-8 (21 to 28)
// Quadrant 3: Lower Left (31-38) - displayed 1-8 (31 to 38)
// Quadrant 4: Lower Right (41-48) - displayed 8-1 (48 to 41)
const PERMANENT_TEETH = {
  quadrant1: [
    { number: 11, name: 'Central Incisor' },
    { number: 12, name: 'Lateral Incisor' },
    { number: 13, name: 'Canine' },
    { number: 14, name: 'First Premolar' },
    { number: 15, name: 'Second Premolar' },
    { number: 16, name: 'First Molar' },
    { number: 17, name: 'Second Molar' },
    { number: 18, name: 'Third Molar (Wisdom)' }
  ],
  quadrant2: [
    { number: 21, name: 'Central Incisor' },
    { number: 22, name: 'Lateral Incisor' },
    { number: 23, name: 'Canine' },
    { number: 24, name: 'First Premolar' },
    { number: 25, name: 'Second Premolar' },
    { number: 26, name: 'First Molar' },
    { number: 27, name: 'Second Molar' },
    { number: 28, name: 'Third Molar (Wisdom)' }
  ],
  quadrant3: [
    { number: 31, name: 'Central Incisor' },
    { number: 32, name: 'Lateral Incisor' },
    { number: 33, name: 'Canine' },
    { number: 34, name: 'First Premolar' },
    { number: 35, name: 'Second Premolar' },
    { number: 36, name: 'First Molar' },
    { number: 37, name: 'Second Molar' },
    { number: 38, name: 'Third Molar (Wisdom)' }
  ],
  quadrant4: [
    { number: 41, name: 'Central Incisor' },
    { number: 42, name: 'Lateral Incisor' },
    { number: 43, name: 'Canine' },
    { number: 44, name: 'First Premolar' },
    { number: 45, name: 'Second Premolar' },
    { number: 46, name: 'First Molar' },
    { number: 47, name: 'Second Molar' },
    { number: 48, name: 'Third Molar (Wisdom)' }
  ]
};

// Helper function to get teeth in display order
// Upper Right (quadrant1): 8-1 (reverse order: 18 to 11)
// Upper Left (quadrant2): 1-8 (normal order: 21 to 28)
// Lower Left (quadrant3): 1-8 (normal order: 31 to 38)
// Lower Right (quadrant4): 8-1 (reverse order: 48 to 41)
const getTeethInDisplayOrder = (quadrant) => {
  const teeth = PERMANENT_TEETH[quadrant];
  if (quadrant === 'quadrant1' || quadrant === 'quadrant4') {
    // Reverse order for upper right and lower right
    return [...teeth].reverse();
  }
  // Normal order for upper left and lower left
  return teeth;
};

const ToothSelector = ({ open, onClose, onConfirm, treatment, initialTeeth = [] }) => {
  const [selectedTeeth, setSelectedTeeth] = useState(initialTeeth);
  const [selectionMode, setSelectionMode] = useState('individual'); // 'individual' or 'quadrant'

  const handleQuadrantSelect = (quadrant) => {
    const quadrantTeeth = PERMANENT_TEETH[quadrant].map(t => t.number);
    setSelectedTeeth(prev => {
      const hasAllQuadrantTeeth = quadrantTeeth.every(t => prev.includes(t));
      if (hasAllQuadrantTeeth) {
        // Remove all quadrant teeth
        return prev.filter(t => !quadrantTeeth.includes(t));
      } else {
        // Add all quadrant teeth
        const newTeeth = [...prev];
        quadrantTeeth.forEach(t => {
          if (!newTeeth.includes(t)) {
            newTeeth.push(t);
          }
        });
        return newTeeth.sort((a, b) => a - b);
      }
    });
  };

  const handleSelectAll = () => {
    const allTeeth = [
      ...PERMANENT_TEETH.quadrant1,
      ...PERMANENT_TEETH.quadrant2,
      ...PERMANENT_TEETH.quadrant3,
      ...PERMANENT_TEETH.quadrant4
    ].map(t => t.number);
    setSelectedTeeth(allTeeth);
  };

  const handleClearAll = () => {
    setSelectedTeeth([]);
  };

  const handleConfirm = () => {
    if (selectedTeeth.length === 0) {
      // If no teeth selected, treat as "not applicable"
      onConfirm([]);
    } else {
      onConfirm(selectedTeeth.sort((a, b) => a - b));
    }
    onClose();
  };

  const calculateTotal = () => {
    if (!treatment) return 0;
    const quantity = selectedTeeth.length || 1; // If no teeth selected, count as 1
    return treatment.price * quantity;
  };

  const renderToothButton = (tooth) => {
    return (
      <ToggleButton
        key={tooth.number}
        value={tooth.number}
        sx={{
          minWidth: 60,
          height: 50
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="body2" fontWeight="bold">
            {tooth.number}
          </Typography>
          <Typography variant="caption" sx={{ fontSize: '0.65rem', display: 'block' }}>
            {tooth.name.split(' ')[0]}
          </Typography>
        </Box>
      </ToggleButton>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        Select Teeth for {treatment?.name || 'Treatment'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Select the teeth this treatment will be applied to. The cost will be calculated based on the number of selected teeth.
          </Typography>
        </Box>

        {/* Selection Mode Toggle */}
        <Box sx={{ mb: 3 }}>
          <FormControl fullWidth>
            <InputLabel>Selection Mode</InputLabel>
            <Select
              value={selectionMode}
              onChange={(e) => setSelectionMode(e.target.value)}
              label="Selection Mode"
            >
              <MenuItem value="individual">Individual Teeth</MenuItem>
              <MenuItem value="quadrant">By Quadrant</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {selectionMode === 'quadrant' ? (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => handleQuadrantSelect('quadrant1')}
                sx={{
                  border: '2px solid',
                  borderColor: PERMANENT_TEETH.quadrant1.every(t => selectedTeeth.includes(t.number)) ? '#1976d2' : '#e0e0e0',
                  backgroundColor: PERMANENT_TEETH.quadrant1.every(t => selectedTeeth.includes(t.number)) ? '#bbdefb' : 'white'
                }}
              >
                <Box>
                  <Typography variant="subtitle2">Upper Right</Typography>
                  <Typography variant="caption">11-18</Typography>
                </Box>
              </Button>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => handleQuadrantSelect('quadrant2')}
                sx={{
                  border: '2px solid',
                  borderColor: PERMANENT_TEETH.quadrant2.every(t => selectedTeeth.includes(t.number)) ? '#1976d2' : '#e0e0e0',
                  backgroundColor: PERMANENT_TEETH.quadrant2.every(t => selectedTeeth.includes(t.number)) ? '#bbdefb' : 'white'
                }}
              >
                <Box>
                  <Typography variant="subtitle2">Upper Left</Typography>
                  <Typography variant="caption">21-28</Typography>
                </Box>
              </Button>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => handleQuadrantSelect('quadrant3')}
                sx={{
                  border: '2px solid',
                  borderColor: PERMANENT_TEETH.quadrant3.every(t => selectedTeeth.includes(t.number)) ? '#1976d2' : '#e0e0e0',
                  backgroundColor: PERMANENT_TEETH.quadrant3.every(t => selectedTeeth.includes(t.number)) ? '#bbdefb' : 'white'
                }}
              >
                <Box>
                  <Typography variant="subtitle2">Lower Left</Typography>
                  <Typography variant="caption">31-38</Typography>
                </Box>
              </Button>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => handleQuadrantSelect('quadrant4')}
                sx={{
                  border: '2px solid',
                  borderColor: PERMANENT_TEETH.quadrant4.every(t => selectedTeeth.includes(t.number)) ? '#1976d2' : '#e0e0e0',
                  backgroundColor: PERMANENT_TEETH.quadrant4.every(t => selectedTeeth.includes(t.number)) ? '#bbdefb' : 'white'
                }}
              >
                <Box>
                  <Typography variant="subtitle2">Lower Right</Typography>
                  <Typography variant="caption">41-48</Typography>
                </Box>
              </Button>
            </Grid>
          </Grid>
        ) : null}

        {/* Tooth Chart */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Upper Jaw
          </Typography>
          <Grid container spacing={1} sx={{ mb: 2 }}>
            {/* Upper Right (Quadrant 1) */}
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary" gutterBottom>
                Upper Right
              </Typography>
              <ToggleButtonGroup
                value={selectedTeeth}
                onChange={(event, newValue) => setSelectedTeeth(newValue)}
                exclusive={false}
                sx={{ flexWrap: 'wrap', gap: 0.5 }}
              >
                {getTeethInDisplayOrder('quadrant1').map(tooth => renderToothButton(tooth))}
              </ToggleButtonGroup>
            </Grid>
            {/* Upper Left (Quadrant 2) */}
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary" gutterBottom>
                Upper Left
              </Typography>
              <ToggleButtonGroup
                value={selectedTeeth}
                onChange={(event, newValue) => setSelectedTeeth(newValue)}
                exclusive={false}
                sx={{ flexWrap: 'wrap', gap: 0.5 }}
              >
                {getTeethInDisplayOrder('quadrant2').map(tooth => renderToothButton(tooth))}
              </ToggleButtonGroup>
            </Grid>
          </Grid>

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            Lower Jaw
          </Typography>
          <Grid container spacing={1}>
            {/* Lower Right (Quadrant 4) - 8-1 (48 to 41) */}
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary" gutterBottom>
                Lower Right
              </Typography>
              <ToggleButtonGroup
                value={selectedTeeth}
                onChange={(event, newValue) => setSelectedTeeth(newValue)}
                exclusive={false}
                sx={{ flexWrap: 'wrap', gap: 0.5 }}
              >
                {getTeethInDisplayOrder('quadrant4').map(tooth => renderToothButton(tooth))}
              </ToggleButtonGroup>
            </Grid>
            {/* Lower Left (Quadrant 3) - 1-8 (31 to 38) */}
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary" gutterBottom>
                Lower Left
              </Typography>
              <ToggleButtonGroup
                value={selectedTeeth}
                onChange={(event, newValue) => setSelectedTeeth(newValue)}
                exclusive={false}
                sx={{ flexWrap: 'wrap', gap: 0.5 }}
              >
                {getTeethInDisplayOrder('quadrant3').map(tooth => renderToothButton(tooth))}
              </ToggleButtonGroup>
            </Grid>
          </Grid>
        </Box>

        {/* Quick Actions */}
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <Button size="small" variant="outlined" onClick={handleSelectAll}>
            Select All
          </Button>
          <Button size="small" variant="outlined" onClick={handleClearAll}>
            Clear All
          </Button>
        </Box>

        {/* Selected Teeth Display */}
        {selectedTeeth.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Selected Teeth ({selectedTeeth.length}):
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {selectedTeeth.map(tooth => (
                <Chip key={tooth} label={tooth} size="small" />
              ))}
            </Box>
          </Box>
        )}

        {/* Cost Calculation */}
        {treatment && (
          <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Unit Price: {formatCurrencySync(treatment.price)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Quantity: {selectedTeeth.length || 1} {selectedTeeth.length === 1 ? 'tooth' : 'teeth'}
            </Typography>
            <Typography variant="h6" color="primary">
              Total: {formatCurrencySync(calculateTotal())}
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleConfirm} variant="contained" color="primary">
          Confirm ({selectedTeeth.length || 1} {selectedTeeth.length === 1 ? 'tooth' : 'teeth'})
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ToothSelector;

